import { useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useDispatch } from 'react-redux';
import { useLocation } from 'react-router-dom';
import { useQueryClient } from '@tanstack/react-query';

import { getErrorMessageFromAPI } from '@/utilz';
import { updateOverChargeClaimInProgress } from '@/redux/Claims/OverCharge/OverChargeActions';
import { setAppLoader } from '@/redux/common/CommonActions';
import { aggregateOverChargeClaimData, checkDuplicateOverchargeItem } from '@/utilz/OverChargeClaimUtils';
import AlertModal from '@/views/components/Modals/AlertModal';
import { NOTIFICATION_TYPES, showNotification } from '@/views/components/Modals/notification';
import {
  invalidateOCClaimTabQueries,
  useGetOCClaimItems,
  useGetOCClaimNotes,
  useGetOCClaimSettlements,
} from '@/views/screens/Claims/OverCharge/hooks';

import { routeFileSelector } from '../../../../../../../utilz/i18n';
import Button from '../../../../../../components/Buttons/Button';
import Tab from '../../../../../../components/Tabs/Tab';
import { useCheckDuplicateOCClaim } from '../../../hooks';
import OverChargeDetails from '../Components/OverChargeDetails/OverChargeDetails';
import OverChargeItems from '../Components/OverChargeItems/OverChargeItems';
import OverChargeNotes from '../Components/OverChargeNotes/OverChargeNotes';
import OverChargeSettlements from '../Components/OverChargeSettlements/OverChargeSettlements';

const OverChargeEditTabs = ({ activeKey, claimDetails, handleTabChange }) => {
  const dispatch = useDispatch();
  const queryClient = useQueryClient();
  const { pathname: activeTab } = useLocation();
  const namespace = useMemo(() => routeFileSelector(activeTab), [activeTab]);
  const { t } = useTranslation([namespace, 'translation']);
  const fbOcKey = claimDetails?.fbOcKey;
  const { data: claimItemsData } = useGetOCClaimItems({ fbOcKey });
  const { data: claimNotesData } = useGetOCClaimNotes({ fbOcKey });
  const { data: claimSettlementsData } = useGetOCClaimSettlements({ fbOcKey });

  const [isSaving, setIsSaving] = useState(false);
  const [duplicateClaimText, setDuplicateClaimText] = useState(false);
  const { mutateAsync: checkDuplicateClaim } = useCheckDuplicateOCClaim({ showLoader: false });

  const detailsRef = useRef(null);
  const itemsRef = useRef(null);
  const notesRef = useRef(null);
  const settlementsRef = useRef(null);

  const scrollToFirstError = (error) => {
    if (error?.errorFields?.length > 0) {
      const firstErrorField = error.errorFields[0];
      const fieldName = Array.isArray(firstErrorField.name) ? firstErrorField.name[0] : firstErrorField.name;
      const errorElement = document.querySelector(`[name="${fieldName}"]`);
      if (errorElement) {
        errorElement.scrollIntoView({ behavior: 'smooth', block: 'center' });
      }
    }
  };

  const preferDraftOrPersisted = ({ ref, getter, persisted, map }) => {
    if (ref?.current?.[getter]) {
      try {
        const draftData = ref.current[getter]();
        if (Array.isArray(draftData) && draftData.length > 0) {
          return draftData;
        }
      } catch (error) {
        console.warn(`Error calling ${getter} on ref:`, error);
      }
    }

    if (Array.isArray(persisted) && persisted.length > 0) {
      return map ? persisted.map(map) : persisted;
    }

    return [];
  };

  const handleSave = async () => {
    if (isSaving) {
      return;
    }

    setIsSaving(true);

    try {
      await Promise.all([
        detailsRef.current?.validateForm?.() ?? Promise.resolve(),
        itemsRef.current?.validateAllItems?.() ?? Promise.resolve(),
        notesRef.current?.validateAllNotes?.() ?? Promise.resolve(),
        settlementsRef.current?.validateAllSettlements?.() ?? Promise.resolve(),
      ]);
    } catch (validationError) {
      setIsSaving(false);
      const hasValidationErrors =
        validationError?.errorFields &&
        Array.isArray(validationError.errorFields) &&
        validationError.errorFields.length > 0;

      if (hasValidationErrors) {
        scrollToFirstError(validationError);
        showNotification({
          message: 'Please fix all validation errors before saving.',
          type: NOTIFICATION_TYPES.ERROR,
        });
      } else {
        console.error('Validation error:', validationError);
        showNotification({
          message: 'Please fix all validation errors before saving.',
          type: NOTIFICATION_TYPES.ERROR,
        });
      }
      return;
    }

    try {
      dispatch(setAppLoader(true));

      const detailsData = detailsRef.current?.getFormData();
      const itemsData = preferDraftOrPersisted({
        getter: 'getItemsData',
        persisted: claimItemsData?.items,
        ref: itemsRef,
      }).map((item) => ({ ...item, dirtyFlag: item.dirtyFlag || 'N' }));

      const notesData = preferDraftOrPersisted({
        getter: 'getNotesData',
        persisted: claimNotesData?.notes,
        ref: notesRef,
      }).map((note) => ({ ...note, dirtyFlag: note.dirtyFlag || 'N' }));

      const settlementsData = preferDraftOrPersisted({
        getter: 'getSettlementsData',
        map: (settlement) => ({ ...settlement, amount: Number(settlement.amount) }),
        persisted: claimSettlementsData?.flatSettlements,
        ref: settlementsRef,
      }).map((settlement) => ({ ...settlement, dirtyFlag: settlement.dirtyFlag || 'N' }));

      const mergedDetailsData = { ...claimDetails, ...detailsData };

      const formData = aggregateOverChargeClaimData(mergedDetailsData, itemsData, notesData, settlementsData);
      formData.isRefetchDetails = true;

      const duplicateValue = await checkDuplicateClaim(formData);
      const { isHavingDuplicateItem, duplicateMessage } = checkDuplicateOverchargeItem(
        formData.claimItem,
        String(duplicateValue),
      );

      if (isHavingDuplicateItem) {
        dispatch(setAppLoader(false));
        setDuplicateClaimText(duplicateMessage);
        setIsSaving(false);
        return;
      }

      dispatch(
        updateOverChargeClaimInProgress({
          ...formData,
          handleSuccess: () => {
            queryClient.invalidateQueries({
              exact: false,
              queryKey: ['claims-over-charge-details', formData.fbOcKey],
            });
            queryClient.invalidateQueries({ exact: false, queryKey: ['claims-over-charge-list'] });
            invalidateOCClaimTabQueries(queryClient, fbOcKey);
          },
        }),
      );
    } catch (error) {
      dispatch(setAppLoader(false));
      console.error('Save error:', error);
      showNotification({
        message: getErrorMessageFromAPI(error) || 'An error occurred while saving. Please try again.',
        type: NOTIFICATION_TYPES.ERROR,
      });
    } finally {
      setIsSaving(false);
    }
  };

  const tabs = useMemo(
    () => [
      {
        content: <OverChargeDetails ref={detailsRef} claimDetails={claimDetails} />,
        contentKey: '1',
        title: t('translation:claims.Details'),
      },
      {
        content: <OverChargeItems ref={itemsRef} claimDetails={claimDetails} />,
        contentKey: '2',
        title: t('translation:claims.overchargeClaimItems'),
      },
      {
        content: <OverChargeNotes ref={notesRef} claimDetails={claimDetails} />,
        contentKey: '3',
        title: t('translation:claims.overchargeClaimNotes'),
      },
      {
        content: <OverChargeSettlements ref={settlementsRef} claimDetails={claimDetails} />,
        contentKey: '4',
        title: t('translation:claims.overchargeClaimSettlements'),
      },
    ],
    [t, claimDetails],
  );

  return (
    <div className="d-flex flex-direction-column h-100">
      <Tab
        onChange={handleTabChange}
        contents={tabs}
        defaultActiveKey="1"
        activeKey={activeKey}
        tabBarExtraContent={
          <Button onClick={handleSave} disabled={isSaving} type="primary" variant="success">
            {t('translation:default.buttons.save')}
          </Button>
        }
      />

      <AlertModal
        open={Boolean(duplicateClaimText)}
        description={duplicateClaimText}
        onOk={() => setDuplicateClaimText(false)}
      />
    </div>
  );
};

export default OverChargeEditTabs;
